/**
 * What this browser knows about the sign-in, outside the cookie.
 *
 * The refresh cookie is HttpOnly, so the page cannot see whether there is
 * one, let alone who it belongs to. Two small marks stand in for that:
 *
 * - The tab mark, in sessionStorage, says this tab was signed in, or was
 *   allowed to pick a sign-in up. sessionStorage is per tab and goes with
 *   it, so a new tab, or a browser opened fresh, has none.
 * - The device mark, in localStorage, says somebody ticked "Keep me signed
 *   in on this device". It outlives the tab, so any tab may pick the
 *   sign-in up.
 *
 * Neither is a credential: without the cookie they open nothing. They decide
 * one thing, whether a tab that has just loaded should try to refresh or
 * should clear the cookie and show the sign-in page. The counter tablets are
 * shared, and for a tab nobody signed into the second is the right answer.
 *
 * Storage can be blocked. Then nothing is remembered and every load shows
 * the sign-in page, which errs the right way.
 */

const TAB_KEY = "hrsys.auth.tab";
const DEVICE_KEY = "hrsys.auth.remembered";
/** Why the sign-in page is showing, for the one line it says about it. */
const REASON_KEY = "hrsys.auth.signed-out-after";

function read(storage: () => Storage, key: string): string | null {
  try {
    return storage().getItem(key);
  } catch {
    return null;
  }
}

function write(storage: () => Storage, key: string, value: string | null): void {
  try {
    if (value === null) storage().removeItem(key);
    else storage().setItem(key, value);
  } catch {
    // Blocked storage: the mark is not made, and the tab is treated as new.
  }
}

const session = () => sessionStorage;
const local = () => localStorage;

/** This tab has a sign-in of its own, until it is closed. */
export function markTab(): void {
  write(session, TAB_KEY, "1");
}

export function hasTabMark(): boolean {
  return read(session, TAB_KEY) === "1";
}

/** Somebody ticked the box, or unticked it, on this device. */
export function rememberDevice(remember: boolean): void {
  write(local, DEVICE_KEY, remember ? "1" : null);
}

export function isDeviceRemembered(): boolean {
  return read(local, DEVICE_KEY) === "1";
}

/**
 * Whether a tab that has just loaded may trade the cookie for a session.
 *
 * Yes for a tab that was signed in and reloaded, and for any tab on a
 * remembered device. No for a fresh tab on a device nobody asked to be
 * remembered on: whatever cookie the browser still holds is a leftover.
 */
export function mayRestoreSession(): boolean {
  return isDeviceRemembered() || hasTabMark();
}

/** Signing out: the tab mark and the device mark both go. */
export function forgetSignIn(): void {
  write(session, TAB_KEY, null);
  write(local, DEVICE_KEY, null);
}

/** Left for the sign-in page by a tab that signed itself out for being idle. */
export function noteSignedOutAfter(minutes: number): void {
  write(session, REASON_KEY, String(minutes));
}

export function readSignedOutAfter(): number | null {
  const raw = read(session, REASON_KEY);
  const minutes = raw === null ? NaN : Number(raw);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : null;
}

export function clearSignedOutAfter(): void {
  write(session, REASON_KEY, null);
}
